"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { cn } from "@marketing-sites/utils/cn";
import { Link, Section } from "@marketing-sites/ui/primitives";
import { type TocItem } from "./reasons-shared";

interface ReasonsTocProps {
  /** One entry per reason. `href` is the in-page anchor for that section. */
  items: TocItem[];
}

/**
 * Hero table-of-contents: the six reasons as a numbered list (each a jump link
 * to its section) beside a large square image. The highlighted reason
 * auto-advances every 2s, crossfading the panel to that reason's image;
 * hovering or keyboard-focusing a row pauses the cycle and takes manual
 * control. Every image is rendered and toggled by opacity so the swap is
 * instant with no per-hover network fetch.
 */
export default function ReasonsTOC({ items }: ReasonsTocProps) {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-advance the highlight through the list; pause on hover/focus and skip
  // entirely when the user prefers reduced motion (WCAG 2.2.2).
  useEffect(() => {
    if (isPaused) return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 2000);
    return () => clearInterval(id);
  }, [isPaused, items.length]);

  // Only resume once focus truly leaves the list (not on row-to-row tabbing).
  const handleBlur = (event: React.FocusEvent<HTMLOListElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsPaused(false);
    }
  };

  return (
    <Section
      id="reasons"
      data-analytics-region="Reasons overview"
      data-analytics-id="sec_why_choose_intercom_reasons_toc"
      gridCols="10"
      dotDecorators={false}
      className="pb-12 pt-14 md:pb-20 md:pt-20"
    >
      <div className="col-span-full grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
        <ol
          className="flex flex-col"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={handleBlur}
        >
          {items.map((item, index) => (
            <li
              key={item.href}
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              className="border-off-black/10 border-t"
            >
              <Link
                // eslint-disable-next-line analytics/id-must-be-static -- each item in tocItems carries a hardcoded analyticsId
                analyticsId={item.analyticsId}
                href={item.href}
                className="group flex items-center gap-5 py-[0.875rem]"
              >
                <span
                  className={cn(
                    "text-heading-6 flex size-7 shrink-0 items-center justify-center tabular-nums transition-colors duration-300",
                    // The active row keeps its blue marker; the rest recede to clay.
                    index !== active
                      ? "bg-clay text-off-black"
                      : "bg-helpdesk text-white",
                  )}
                >
                  {index + 1}
                </span>
                <span
                  className={cn(
                    "text-[1.125rem] leading-tight tracking-[-0.02rem] transition-colors",
                    index === active ? "text-current" : "text-current/60",
                  )}
                >
                  {item.label}
                </span>
              </Link>
            </li>
          ))}
        </ol>

        <div className="relative aspect-square w-full lg:ml-auto lg:max-w-[90%]">
          {/* Every layer loads eagerly: they're stacked and toggled by opacity,
              so the hover crossfade must be instant with no per-hover fetch.
              Lazy loading lets the browser defer the occluded (opacity-0) layers,
              which left some backgrounds blank. */}
          {items.map((item, index) => (
            <Image
              key={item.href}
              src={item.background}
              alt={index === active ? item.label : ""}
              fill
              loading="eager"
              sizes="(min-width: 1024px) 50vw, 100vw"
              aria-hidden={index === active ? undefined : true}
              className={cn(
                "object-cover transition-opacity duration-300 ease-out",
                index === active ? "opacity-100" : "opacity-0",
              )}
            />
          ))}
          {items.map((item, index) => (
            <Image
              key={`${item.href}-fg`}
              src={item.foreground}
              alt=""
              aria-hidden
              fill
              loading="eager"
              sizes="(min-width: 1024px) 50vw, 100vw"
              className={cn(
                "object-contain transition-opacity duration-300 ease-out",
                index === active ? "opacity-100" : "opacity-0",
              )}
            />
          ))}
        </div>
      </div>
    </Section>
  );
}
